import api from "ApiClient";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { common } from "utils/Wording";
import { ApiURL } from "utils/Actions";
import { API_RESPONSE_STATUS } from "utils/Constants";
import { showErrorModal } from "utils/hooks/handleNotification";

export type EASDocListType = {
  studentNumber?: string;
  documentName: string;
  fileReference?: string;
  dateSubmitted?: string;
};

export type EASResultType = {
  studentNumber: string;
  givenName1: string;
  givenName2?: string;
  familyName: string;
  easApplicantDocuments: EASDocListType[];
};

type EASSliceType = {
  searchName?: {
    firstName: string;
    familyName: string;
  };
  searchID?: {
    studentNum: string;
  };
  data?: EASResultType[];
  docData: EASDocListType[];
  isLoading: boolean;
  isLoadingError: boolean;
  isDocLoading: boolean;
  isDocLoadingError: boolean;
};

const initialState: EASSliceType = {
  data: [],
  docData: [],
  isLoading: false,
  isLoadingError: false,
  isDocLoading: false,
  isDocLoadingError: false
};

export const fetchEASStudents = createAsyncThunk("/eas", async (searchTerm: any) => {
  if (!searchTerm) {
    throw new Error(common.loadingError);
  }
  try {
    const res = await api.get(ApiURL.eas, {
      params: searchTerm
    });
    if (res?.data?.status === API_RESPONSE_STATUS.ERROR) {
      throw new Error(common.loadingError);
    }
    return res?.data?.data ? res.data.data : [];
  } catch (error: any) {
    console.error(error);
    showErrorModal({
      title: common.failAsync,
      errors: error.response?.data?.errors ? error.response.data.errors : [error.message]
    });
    return initialState.data;
  }
});

export const fetchEASSummary = createAsyncThunk("/easSummary", async () => {
  try {
    const res = await api.get(ApiURL.easSummary);
    if (res?.data?.status === API_RESPONSE_STATUS.ERROR) {
      throw new Error(common.loadingError);
    }
    return res?.data?.data ? res.data.data : [];
  } catch (error: any) {
    console.error(error);
    showErrorModal({
      title: common.failAsync,
      errors: error.response?.data?.errors ? error.response.data.errors : [error.message]
    });
    return initialState.docData;
  }
});

export const AppStatusSlice = createSlice({
  name: "eas",
  initialState,
  reducers: {
    resetEASResult: (state, action) => {
      return {
        ...state,
        data: action.payload,
        isLoading: false,
        isLoadingError: false
      };
    },
    setSearchName: (state, action) => {
      return {
        ...state,
        searchName: action.payload,
        searchID: undefined
      };
    },
    setSearchID: (state, action) => {
      return {
        ...state,
        searchID: action.payload,
        searchName: undefined
      };
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchEASStudents.pending, state => {
      state.isLoading = true;
      state.isLoadingError = false;
    });
    builder.addCase(fetchEASStudents.fulfilled, (state, action) => {
      return {
        ...state,
        data: action.payload,
        isLoading: false,
        isLoadingError: false
      };
    });
    builder.addCase(fetchEASStudents.rejected, () => {
      return {
        data: initialState.data,
        docData: initialState.docData,
        isLoading: false,
        isLoadingError: true,
        isDocLoading: false,
        isDocLoadingError: false
      };
    });
    builder.addCase(fetchEASSummary.pending, state => {
      state.isDocLoading = true;
      state.isDocLoadingError = false;
    });
    builder.addCase(fetchEASSummary.fulfilled, (state, action) => {
      return {
        ...state,
        docData: action.payload,
        isDocLoading: false,
        isDocLoadingError: false
      };
    });
    builder.addCase(fetchEASSummary.rejected, () => {
      return {
        data: initialState.data,
        docData: initialState.docData,
        isLoading: false,
        isLoadingError: false,
        isDocLoading: false,
        isDocLoadingError: true
      };
    });
  }
});

export const { resetEASResult, setSearchName, setSearchID } = AppStatusSlice.actions;

export default AppStatusSlice.reducer;
